import React, { useEffect, useRef, useState } from 'react';
import Avatar from '@mui/material/Avatar';
import AvatarGroup from '@mui/material/AvatarGroup';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import { getCollectionColors } from '../utils/collectionColors';

type Variant = 'small' | 'medium' | 'large';

interface CollectionItem {
  id: string;
  name: string;
  isDefault?: boolean;
  color?: string;
}

interface CollectionAvatarsProps {
  // array of collection objects (id + display name)
  collections: CollectionItem[];
  variant?: Variant;
  maxVisible?: number; // how many to show before grouping (default from variant)
  // collectionId passed as number when avatar clicked
  onAvatarClick: (collectionId: number) => void;
  // optional: when provided, clicking +N triggers this instead of expanding
  onOverflowClick?: () => void;
  onAddToCollection?: () => void;
  // optional: when provided, clicking + with non-empty text will create this collection
  // and add the current figure to it (implemented in parent)
  onCreateCollectionAndAdd?: (name: string) => Promise<void> | void;
}

const variantConfig: Record<Variant, { size: number; fontSize: number; maxVisibleDefault: number; rounded: number }> = {
  small: { size: 28, fontSize: 12, maxVisibleDefault: 5, rounded: 14 },
  medium: { size: 40, fontSize: 14, maxVisibleDefault: 6, rounded: 20 },
  // large uses same rectangular style as medium (per request)
  large: { size: 40, fontSize: 14, maxVisibleDefault: 6, rounded: 20 },
};

function makeAbbrev(name: string, variant: Variant) {
  if (!name) return '';
  if (variant === 'small') {
    // 2-char abbreviation from words or chars
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
    return name.slice(0, 2).toUpperCase();
  }
  // medium/large: up to 8 characters
  return name.slice(0, 8);
}

const CollectionAvatars: React.FC<CollectionAvatarsProps> = ({
  collections,
  variant = 'medium',
  maxVisible,
  onAvatarClick,
  onOverflowClick,
  onAddToCollection,
  onCreateCollectionAndAdd,
}) => {
  const cfg = variantConfig[variant];
  const showCount = maxVisible ?? cfg.maxVisibleDefault;

  const [expanded, setExpanded] = useState(false);
  const hasNoCollections = !collections || collections.length === 0;
  const visible = expanded ? collections : collections.slice(0, showCount);
  const hiddenCount = expanded ? 0 : Math.max(0, collections.length - showCount);

  const isSmall = variant === 'small';
  const addHeight = isSmall ? cfg.size : 24;
  const addOffset = isSmall ? 2 : 0;

  const [addFieldValue, setAddFieldValue] = useState('');

  const handleAddToCollection = async () => {
    const name = addFieldValue.trim();
    if (name && onCreateCollectionAndAdd) {
      await onCreateCollectionAndAdd(name);
      setAddFieldValue('');
      return;
    }
    if (!onAddToCollection) return;
    onAddToCollection();
    setAddFieldValue('');
  };

  // call provided callback with numeric id; stop bubbling to parent
  const handleAvatarClick = (collectionId: string, e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    onAvatarClick(Number(collectionId));
  };

  // Measure container width for medium/large to cap to two rows
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState<number>(0);

  useEffect(() => {
    if (!containerRef.current) return;
    const ro = new ResizeObserver((entries) => {
      const cr = entries[0]?.contentRect;
      if (cr) setContainerWidth(cr.width);
    });
    ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, []);

  if (hasNoCollections) return null;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }} ref={containerRef}>
      {isSmall ? (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <AvatarGroup
            max={999}
            sx={{
              '& .MuiAvatar-root': {
                width: cfg.size,
                height: cfg.size,
                fontSize: cfg.fontSize,
                marginLeft: '-9px',
                border: '2px solid white',
              },
              '& .MuiAvatar-root:first-of-type': {
                marginLeft: 0,
              },
            }}>
            {visible.map((c, idx) => (
              <Tooltip key={c.id ?? idx} title={c.name} arrow>
                <IconButton
                  onClick={(e) => handleAvatarClick(c.id, e)}
                  aria-label={`collection-${c.id}`}
                  size="small"
                  sx={{ padding: 0, marginLeft: idx === 0 ? 0 : '-9px' }}>
                  {(() => {
                    const colors = getCollectionColors(c);
                    return (
                      <Avatar
                        sx={{
                          width: cfg.size,
                          height: cfg.size,
                          fontSize: cfg.fontSize,
                          backgroundColor: colors.bg,
                          color: colors.color,
                          border: `2px solid white`,
                        }}>
                        {makeAbbrev(c.name, variant)}
                      </Avatar>
                    );
                  })()}
                </IconButton>
              </Tooltip>
            ))}
          </AvatarGroup>
          {hiddenCount > 0 && (
            <Tooltip title={`Vis ${hiddenCount} flere`} arrow>
              <Avatar
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOverflowClick) {
                    onOverflowClick();
                  } else {
                    setExpanded(true);
                  }
                }}
                sx={(theme) => ({
                  width: cfg.size,
                  height: cfg.size,
                  fontSize: cfg.fontSize,
                  backgroundColor: theme.palette.grey[100],
                  color: '#000',
                  border: `2px solid white`,
                  cursor: 'pointer',
                  userSelect: 'none',
                  marginLeft: '-9px',
                })}>
                +{hiddenCount}
              </Avatar>
            </Tooltip>
          )}
        </Box>
      ) : (
        (() => {
          // Estimate chips per row and cap to two rows. Reserve one spot for +N
          const estimatedChipWidth = 90; // px incl. typical gap
          const rows = 2;
          const perRow = Math.max(1, Math.floor((containerWidth || 0) / estimatedChipWidth));
          const capacity = rows * perRow;
          const needsOverflow = !expanded && collections.length > capacity;
          const dynamicCount = expanded ? collections.length : needsOverflow ? Math.max(0, capacity - 1) : Math.min(collections.length, capacity);

          const visibleMd = expanded ? collections : collections.slice(0, dynamicCount);
          const hiddenCountMd = expanded ? 0 : Math.max(0, collections.length - dynamicCount);

          return (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              {visibleMd.map((c, idx) => (
                <Tooltip key={c.id ?? idx} title={c.name} arrow>
                  <IconButton onClick={(e) => handleAvatarClick(c.id, e)} aria-label={`collection-${c.id}`} size="small" sx={{ padding: 0 }}>
                    {(() => {
                      const colors = getCollectionColors(c);
                      return (
                        <Avatar
                          variant="rounded"
                          sx={(theme) => ({
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 'auto',
                            height: 24,
                            fontSize: cfg.fontSize,
                            px: 0.75,
                            borderRadius: 6,
                            backgroundColor: colors.bg,
                            color: colors.color,
                            border: `1px solid ${theme.palette.divider}`,
                            boxShadow: 'none',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            // limit width to keep layout compact in two rows
                            maxWidth: '14ch',
                          })}>
                          <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {c.name}
                          </Box>
                        </Avatar>
                      );
                    })()}
                  </IconButton>
                </Tooltip>
              ))}
              {hiddenCountMd > 0 && (
                <Tooltip title={`Vis ${hiddenCountMd} flere`} arrow>
                  <Avatar
                    variant="rounded"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onOverflowClick) {
                        onOverflowClick();
                      } else {
                        setExpanded(true);
                      }
                    }}
                    sx={(theme) => ({
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 'auto',
                      height: 24,
                      fontSize: cfg.fontSize,
                      px: 0.75,
                      borderRadius: 6,
                      backgroundColor: theme.palette.grey[100],
                      color: '#000',
                      border: `1px solid ${theme.palette.divider}`,
                      cursor: 'pointer',
                      userSelect: 'none',
                    })}>
                    +{hiddenCountMd}
                  </Avatar>
                </Tooltip>
              )}
              {onAddToCollection && (
                <TextField
                  value={addFieldValue}
                  onChange={(e) => setAddFieldValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddToCollection();
                    }
                  }}
                  variant="standard"
                  sx={{
                    ml: 0.5,
                    flexShrink: 0,
                    width: 'auto',
                    maxWidth: 'fit-content',
                    alignSelf: 'flex-end',
                    '& .MuiInputBase-root': {
                      display: 'flex',
                      alignItems: 'center',
                      height: addHeight,
                      backgroundColor: 'transparent',
                      color: 'inherit',
                      px: 0.5,
                      position: 'relative',
                      top: addOffset,
                    },
                    '& .MuiInputAdornment-root': {
                      alignItems: 'center',
                    },
                    '& .MuiInputBase-input': {
                      padding: 0,
                      fontSize: cfg.fontSize,
                      width: '14ch',
                      maxWidth: '14ch',
                    },
                    '& .MuiInput-underline:before': {
                      borderBottomColor: (theme) => theme.palette.grey[600],
                    },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                      borderBottomColor: (theme) => theme.palette.grey[700],
                    },
                    '& .MuiInput-underline:after': {
                      borderBottomColor: (theme) => theme.palette.grey[800],
                    },
                  }}
                  inputProps={{ 'aria-label': 'Legg til i samling', placeholder: 'Legg til i samling' }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={handleAddToCollection} aria-label="Legg til i samling">
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            </Box>
          );
        })()
      )}
      {onAddToCollection && isSmall && (
        <TextField
          value={addFieldValue}
          onChange={(e) => setAddFieldValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleAddToCollection();
            }
          }}
          variant="standard"
          sx={{
            ml: isSmall ? 0.25 : 0.5,
            flexShrink: 0,
            width: 'auto',
            alignSelf: 'flex-end',
            '& .MuiInputBase-root': {
              display: 'flex',
              alignItems: 'center',
              height: addHeight,
              backgroundColor: 'transparent',
              color: 'inherit',
              px: 0.5,
              position: 'relative',
              top: addOffset,
            },
            '& .MuiInputAdornment-root': {
              alignItems: 'center',
            },
            '& .MuiInputBase-input': {
              padding: 0,
              fontSize: cfg.fontSize,
              width: '18ch',
              maxWidth: '18ch',
            },
            '& .MuiInput-underline:before': {
              borderBottomColor: (theme) => theme.palette.grey[600],
            },
            '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
              borderBottomColor: (theme) => theme.palette.grey[700],
            },
            '& .MuiInput-underline:after': {
              borderBottomColor: (theme) => theme.palette.grey[800],
            },
          }}
          inputProps={{ 'aria-label': 'Legg til i samling' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 1, alignItems: 'center' }}>
                <Typography variant="body2" sx={{ fontSize: cfg.fontSize, color: 'text.secondary', lineHeight: 1 }}>
                  Legg til i samling
                </Typography>
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleAddToCollection} aria-label="Legg til i samling">
                  <AddIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
    </Box>
  );
};

export default CollectionAvatars;
